import AgentDefs from '@/components/agent/agentDefs';
import DataSourceDefs from '@/components/dataSource/dataSourceDefs';
import TabPanel from '@/components/tabPanel';
import UserAppDefs from '@/components/userApp/userAppDefs';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, Stack, Typography, useTheme, alpha, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserContext } from '@/contexts/userContext';
import AppsIcon from '@mui/icons-material/Apps';
import StorageIcon from '@mui/icons-material/Storage';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BuildIcon from '@mui/icons-material/Build';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DomainIcon from '@mui/icons-material/Domain';
import TenantSettings from './tenantSettings';
import AdminActions from './adminActions';
import PipelineBlueprintDefs from '@/components/pipelineBlueprint/pipelineBlueprintDefs';
import DomainManagement from '@/components/domain/domainManagement';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ProgramManagement from '@/components/program/programManagement';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LlmModelManagement from '@/components/llmModel/llmModelManagement';
import TenantManagement from '@/components/tenant/tenantManagement';
import BusinessIcon from '@mui/icons-material/Business';
import ShieldIcon from '@mui/icons-material/Shield';
import { Chip, Divider } from '@mui/material';

type Props = { maxHeight: number };

const Administration = ({}: Props) => {
  const [containerRef, containerSize] = useElementSize();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, hasRole, isSuperAdmin } = useUserContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const adminTab = searchParams.get('adminTab');

  const initialTab = (() => {
    if (adminTab === 'tenants' || adminTab === 'tenantManagement') {
      return isSuperAdmin ? 'tenants' : 'userApps';
    }
    if (adminTab === 'llmModels' || adminTab === 'models') {
      return isSuperAdmin ? 'llmModels' : 'userApps';
    }
    if (adminTab === 'domains' || adminTab === 'domainManagement') {
      return isSuperAdmin ? 'domains' : 'userApps';
    }
    if (adminTab === 'adminActions') {
      return isSuperAdmin ? 'adminActions' : 'userApps';
    }
    if (adminTab === 'pipelineBlueprints' || adminTab === 'blueprints') {
      return 'pipelineBlueprints';
    }
    if (adminTab === 'programs' || adminTab === 'programManagement') {
      return 'programs';
    }
    return adminTab || 'userApps';
  })();

  const [selectedTab, setSelectedTab] = useState<any>(initialTab);

  const handleTabChange = (newValue: string) => {
    setSearchParams({ adminTab: newValue });
    setSelectedTab(newValue);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        flexWrap: 'nowrap',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
      }}
    >
      {/* Sidebar nav */}
      <Box
        sx={{
          borderRight: { xs: 0, md: 1 },
          borderBottom: { xs: 1, md: 0 },
          borderColor: 'divider',
          minWidth: { xs: '100%', md: 200 },
          display: 'flex',
          flexDirection: 'column',
          // Scroll the options on a short screen instead of cutting off the last ones (Platform Admin)
          minHeight: 0,
          overflowY: { xs: 'visible', md: 'auto' },
          flexShrink: 0,
          gap: 2,
          pr: { xs: 0, md: 1.5 },
          pb: { xs: 1.5, md: 0 },
          py: 0.5,
        }}
      >
        <Box
          sx={{
            px: 1.5,
            pb: 1,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <AdminPanelSettingsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 750,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontSize: '10px',
            }}
          >
            System Settings
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'row', md: 'column' }}
          spacing={1}
          sx={{
            overflowX: { xs: 'auto', md: 'visible' },
            pb: { xs: 0.5, md: 0 },
            width: '100%',
            flexShrink: 0,
            '&::-webkit-scrollbar': { display: 'none' }, // hide scrollbar for clean aesthetics
          }}
        >
          {hasPermission('pipeline:apps:view') && (
            <Button
              fullWidth={!isMobile}
              onClick={() => handleTabChange('userApps')}
              startIcon={<AppsIcon sx={{ fontSize: 16 }} />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                px: 1.75,
                py: 1.25,
                borderRadius: '12px',
                bgcolor: selectedTab === 'userApps' ? alpha(theme.palette.secondary.main, 0.08) : 'transparent',
                color: selectedTab === 'userApps' ? 'text.primary' : 'text.secondary',
                fontWeight: selectedTab === 'userApps' ? 700 : 550,
                fontSize: '0.8rem',
                border: '1px solid',
                borderColor: selectedTab === 'userApps' ? alpha(theme.palette.secondary.main, 0.15) : 'transparent',
                whiteSpace: 'nowrap',
                minWidth: 'max-content',
                '&:hover': {
                  bgcolor:
                    selectedTab === 'userApps' ? alpha(theme.palette.secondary.main, 0.12) : 'rgba(255, 255, 255, 0.1)',
                  borderColor:
                    selectedTab === 'userApps'
                      ? alpha(theme.palette.secondary.main, 0.25)
                      : 'rgba(255, 255, 255, 0.15)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              App Definitions
            </Button>
          )}
          {hasPermission('pipeline:dataSources:view') && (
            <Button
              fullWidth={!isMobile}
              onClick={() => handleTabChange('dataSources')}
              startIcon={<StorageIcon sx={{ fontSize: 16 }} />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                px: 1.75,
                py: 1.25,
                borderRadius: '12px',
                bgcolor: selectedTab === 'dataSources' ? alpha(theme.palette.secondary.main, 0.08) : 'transparent',
                color: selectedTab === 'dataSources' ? 'text.primary' : 'text.secondary',
                fontWeight: selectedTab === 'dataSources' ? 700 : 550,
                fontSize: '0.8rem',
                border: '1px solid',
                borderColor: selectedTab === 'dataSources' ? alpha(theme.palette.secondary.main, 0.15) : 'transparent',
                whiteSpace: 'nowrap',
                minWidth: 'max-content',
                '&:hover': {
                  bgcolor:
                    selectedTab === 'dataSources'
                      ? alpha(theme.palette.secondary.main, 0.12)
                      : 'rgba(255, 255, 255, 0.1)',
                  borderColor:
                    selectedTab === 'dataSources'
                      ? alpha(theme.palette.secondary.main, 0.25)
                      : 'rgba(255, 255, 255, 0.15)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Datasource Defs
            </Button>
          )}
          {hasPermission('pipeline:agents:view') && (
            <Button
              fullWidth={!isMobile}
              onClick={() => handleTabChange('agents')}
              startIcon={<SmartToyIcon sx={{ fontSize: 16 }} />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                px: 1.75,
                py: 1.25,
                borderRadius: '12px',
                bgcolor: selectedTab === 'agents' ? alpha(theme.palette.secondary.main, 0.08) : 'transparent',
                color: selectedTab === 'agents' ? 'text.primary' : 'text.secondary',
                fontWeight: selectedTab === 'agents' ? 700 : 550,
                fontSize: '0.8rem',
                border: '1px solid',
                borderColor: selectedTab === 'agents' ? alpha(theme.palette.secondary.main, 0.15) : 'transparent',
                whiteSpace: 'nowrap',
                minWidth: 'max-content',
                '&:hover': {
                  bgcolor:
                    selectedTab === 'agents' ? alpha(theme.palette.secondary.main, 0.12) : 'rgba(255, 255, 255, 0.1)',
                  borderColor:
                    selectedTab === 'agents' ? alpha(theme.palette.secondary.main, 0.25) : 'rgba(255, 255, 255, 0.15)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Agent Definitions
            </Button>
          )}
          {(hasPermission('pipeline:blueprints:view') ||
            hasPermission('pipeline:view') ||
            hasRole('administrator')) && (
            <Button
              fullWidth={!isMobile}
              onClick={() => handleTabChange('pipelineBlueprints')}
              startIcon={<AccountTreeIcon sx={{ fontSize: 16 }} />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                px: 1.75,
                py: 1.25,
                borderRadius: '12px',
                bgcolor:
                  selectedTab === 'pipelineBlueprints' ? alpha(theme.palette.secondary.main, 0.08) : 'transparent',
                color: selectedTab === 'pipelineBlueprints' ? 'text.primary' : 'text.secondary',
                fontWeight: selectedTab === 'pipelineBlueprints' ? 700 : 550,
                fontSize: '0.8rem',
                border: '1px solid',
                borderColor:
                  selectedTab === 'pipelineBlueprints'
                    ? alpha(theme.palette.secondary.main, 0.15)
                    : 'transparent',
                whiteSpace: 'nowrap',
                minWidth: 'max-content',
                '&:hover': {
                  bgcolor:
                    selectedTab === 'pipelineBlueprints'
                      ? alpha(theme.palette.secondary.main, 0.12)
                      : 'rgba(255, 255, 255, 0.1)',
                  borderColor:
                    selectedTab === 'pipelineBlueprints'
                      ? alpha(theme.palette.secondary.main, 0.25)
                      : 'rgba(255, 255, 255, 0.15)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Pipeline Blueprints
            </Button>
          )}
          {(hasRole('administrator') ||
            hasPermission('tenant:settings:view') ||
            hasPermission('pipeline:view')) && (
            <Button
              fullWidth={!isMobile}
              onClick={() => handleTabChange('programs')}
              startIcon={<BusinessCenterIcon sx={{ fontSize: 16 }} />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                px: 1.75,
                py: 1.25,
                borderRadius: '12px',
                bgcolor: selectedTab === 'programs' ? alpha(theme.palette.secondary.main, 0.08) : 'transparent',
                color: selectedTab === 'programs' ? 'text.primary' : 'text.secondary',
                fontWeight: selectedTab === 'programs' ? 700 : 550,
                fontSize: '0.8rem',
                border: '1px solid',
                borderColor: selectedTab === 'programs' ? alpha(theme.palette.secondary.main, 0.15) : 'transparent',
                whiteSpace: 'nowrap',
                minWidth: 'max-content',
                '&:hover': {
                  bgcolor:
                    selectedTab === 'programs' ? alpha(theme.palette.secondary.main, 0.12) : 'rgba(255, 255, 255, 0.1)',
                  borderColor:
                    selectedTab === 'programs'
                      ? alpha(theme.palette.secondary.main, 0.25)
                      : 'rgba(255, 255, 255, 0.15)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Program Management
            </Button>
          )}

          {hasRole('administrator') && (
            <Button
              fullWidth={!isMobile}
              onClick={() => handleTabChange('settings')}
              startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                px: 1.75,
                py: 1.25,
                borderRadius: '12px',
                bgcolor: selectedTab === 'settings' ? alpha(theme.palette.secondary.main, 0.08) : 'transparent',
                color: selectedTab === 'settings' ? 'text.primary' : 'text.secondary',
                fontWeight: selectedTab === 'settings' ? 700 : 550,
                fontSize: '0.8rem',
                border: '1px solid',
                borderColor: selectedTab === 'settings' ? alpha(theme.palette.secondary.main, 0.15) : 'transparent',
                whiteSpace: 'nowrap',
                minWidth: 'max-content',
                '&:hover': {
                  bgcolor:
                    selectedTab === 'settings' ? alpha(theme.palette.secondary.main, 0.12) : 'rgba(255, 255, 255, 0.1)',
                  borderColor:
                    selectedTab === 'settings'
                      ? alpha(theme.palette.secondary.main, 0.25)
                      : 'rgba(255, 255, 255, 0.15)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Tenant Settings
            </Button>
          )}

          {/* Platform Administration (Super Admin only) */}
          {isSuperAdmin && (
            <>
              <Box
                sx={{
                  px: 1.5,
                  pt: 2,
                  pb: 1,
                  mt: 1,
                  borderTop: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ShieldIcon sx={{ fontSize: 15, color: theme.palette.secondary.main }} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 800,
                      color: theme.palette.secondary.main,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontSize: '10px',
                    }}
                  >
                    Platform Admin
                  </Typography>
                </Stack>
                <Chip
                  label="Super"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '9px',
                    fontWeight: 800,
                    bgcolor: alpha(theme.palette.secondary.main, 0.15),
                    color: theme.palette.secondary.main,
                  }}
                />
              </Box>

              <Button
                fullWidth={!isMobile}
                onClick={() => handleTabChange('tenants')}
                startIcon={<BusinessIcon sx={{ fontSize: 16 }} />}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  px: 1.75,
                  py: 1.25,
                  borderRadius: '12px',
                  bgcolor: selectedTab === 'tenants' ? alpha(theme.palette.secondary.main, 0.08) : 'transparent',
                  color: selectedTab === 'tenants' ? 'text.primary' : 'text.secondary',
                  fontWeight: selectedTab === 'tenants' ? 700 : 550,
                  fontSize: '0.8rem',
                  border: '1px solid',
                  borderColor: selectedTab === 'tenants' ? alpha(theme.palette.secondary.main, 0.15) : 'transparent',
                  whiteSpace: 'nowrap',
                  minWidth: 'max-content',
                  '&:hover': {
                    bgcolor:
                      selectedTab === 'tenants' ? alpha(theme.palette.secondary.main, 0.12) : 'rgba(255, 255, 255, 0.1)',
                    borderColor:
                      selectedTab === 'tenants'
                        ? alpha(theme.palette.secondary.main, 0.25)
                        : 'rgba(255, 255, 255, 0.15)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Tenants
              </Button>

              <Button
                fullWidth={!isMobile}
                onClick={() => handleTabChange('llmModels')}
                startIcon={<PsychologyIcon sx={{ fontSize: 16 }} />}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  px: 1.75,
                  py: 1.25,
                  borderRadius: '12px',
                  bgcolor: selectedTab === 'llmModels' ? alpha(theme.palette.secondary.main, 0.08) : 'transparent',
                  color: selectedTab === 'llmModels' ? 'text.primary' : 'text.secondary',
                  fontWeight: selectedTab === 'llmModels' ? 700 : 550,
                  fontSize: '0.8rem',
                  border: '1px solid',
                  borderColor: selectedTab === 'llmModels' ? alpha(theme.palette.secondary.main, 0.15) : 'transparent',
                  whiteSpace: 'nowrap',
                  minWidth: 'max-content',
                  '&:hover': {
                    bgcolor:
                      selectedTab === 'llmModels' ? alpha(theme.palette.secondary.main, 0.12) : 'rgba(255, 255, 255, 0.1)',
                    borderColor:
                      selectedTab === 'llmModels'
                        ? alpha(theme.palette.secondary.main, 0.25)
                        : 'rgba(255, 255, 255, 0.15)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                LLM Models
              </Button>

              <Button
                fullWidth={!isMobile}
                onClick={() => handleTabChange('domains')}
                startIcon={<DomainIcon sx={{ fontSize: 16 }} />}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  px: 1.75,
                  py: 1.25,
                  borderRadius: '12px',
                  bgcolor: selectedTab === 'domains' ? alpha(theme.palette.secondary.main, 0.08) : 'transparent',
                  color: selectedTab === 'domains' ? 'text.primary' : 'text.secondary',
                  fontWeight: selectedTab === 'domains' ? 700 : 550,
                  fontSize: '0.8rem',
                  border: '1px solid',
                  borderColor: selectedTab === 'domains' ? alpha(theme.palette.secondary.main, 0.15) : 'transparent',
                  whiteSpace: 'nowrap',
                  minWidth: 'max-content',
                  '&:hover': {
                    bgcolor:
                      selectedTab === 'domains' ? alpha(theme.palette.secondary.main, 0.12) : 'rgba(255, 255, 255, 0.1)',
                    borderColor:
                      selectedTab === 'domains'
                        ? alpha(theme.palette.secondary.main, 0.25)
                        : 'rgba(255, 255, 255, 0.15)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Domain Management
              </Button>

              <Button
                fullWidth={!isMobile}
                onClick={() => handleTabChange('adminActions')}
                startIcon={<BuildIcon sx={{ fontSize: 16 }} />}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  px: 1.75,
                  py: 1.25,
                  borderRadius: '12px',
                  bgcolor: selectedTab === 'adminActions' ? alpha(theme.palette.secondary.main, 0.08) : 'transparent',
                  color: selectedTab === 'adminActions' ? 'text.primary' : 'text.secondary',
                  fontWeight: selectedTab === 'adminActions' ? 700 : 550,
                  fontSize: '0.8rem',
                  border: '1px solid',
                  borderColor: selectedTab === 'adminActions' ? alpha(theme.palette.secondary.main, 0.15) : 'transparent',
                  whiteSpace: 'nowrap',
                  minWidth: 'max-content',
                  '&:hover': {
                    bgcolor:
                      selectedTab === 'adminActions' ? alpha(theme.palette.secondary.main, 0.12) : 'rgba(255, 255, 255, 0.1)',
                    borderColor:
                      selectedTab === 'adminActions'
                        ? alpha(theme.palette.secondary.main, 0.25)
                        : 'rgba(255, 255, 255, 0.15)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Admin Actions
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {/* Content area */}
      <Box sx={{ flex: '1 1 0', minWidth: 0, pl: { xs: 0, md: 2.5 }, pt: { xs: 2, md: 0 } }}>
        {hasPermission('pipeline:apps:view') && (
          <TabPanel value="userApps" currentTab={selectedTab} maxHeight={containerSize.height}>
            <UserAppDefs />
          </TabPanel>
        )}
        {hasPermission('pipeline:dataSources:view') && (
          <TabPanel value="dataSources" currentTab={selectedTab} maxHeight={containerSize.height}>
            <DataSourceDefs />
          </TabPanel>
        )}
        {hasPermission('pipeline:agents:view') && (
          <TabPanel value="agents" currentTab={selectedTab} maxHeight={containerSize.height}>
            <AgentDefs />
          </TabPanel>
        )}
        {(hasPermission('pipeline:blueprints:view') ||
          hasPermission('pipeline:view') ||
          hasRole('administrator')) && (
          <TabPanel value="pipelineBlueprints" currentTab={selectedTab} maxHeight={containerSize.height}>
            <PipelineBlueprintDefs maxHeight={containerSize.height} />
          </TabPanel>
        )}
        {(hasRole('administrator') ||
          hasPermission('tenant:settings:view') ||
          hasPermission('pipeline:view')) && (
          <TabPanel value="programs" currentTab={selectedTab} maxHeight={containerSize.height}>
            <ProgramManagement maxHeight={containerSize.height} />
          </TabPanel>
        )}
        {hasRole('administrator') && (
          <TabPanel value="settings" currentTab={selectedTab} maxHeight={containerSize.height}>
            <TenantSettings />
          </TabPanel>
        )}

        {/* Platform tabs - Super Admin only */}
        {isSuperAdmin && (
          <TabPanel value="tenants" currentTab={selectedTab} maxHeight={containerSize.height}>
            <TenantManagement />
          </TabPanel>
        )}
        {isSuperAdmin && (
          <TabPanel value="llmModels" currentTab={selectedTab} maxHeight={containerSize.height}>
            <LlmModelManagement maxHeight={containerSize.height} />
          </TabPanel>
        )}
        {isSuperAdmin && (
          <TabPanel value="domains" currentTab={selectedTab} maxHeight={containerSize.height}>
            <DomainManagement maxHeight={containerSize.height} />
          </TabPanel>
        )}
        {isSuperAdmin && (
          <TabPanel value="adminActions" currentTab={selectedTab} maxHeight={containerSize.height}>
            <AdminActions />
          </TabPanel>
        )}
      </Box>
    </Box>
  );
};

export default Administration;
